import type { VehicleGroup, FlatVehicleDetection } from "../client-messages/vehicles";
import type { TimelineEntry } from "../types";

/**
 * Converts server-side vehicle groups into timeline entries.
 * Single-member groups become individual cards; multi-member groups
 * become expandable group cards.
 */
export function groupsToTimeline(groups: VehicleGroup[]): TimelineEntry[] {
  return groups.map((group) => {
    const [first] = group.members;
    if (group.members.length === 1 && first) {
      return { kind: "single" as const, vehicle: first };
    }
    return { kind: "group" as const, group };
  });
}

/**
 * Buckets a flat vehicle list (from VT v3) into VehicleGroup[] by groupId.
 * All rows sharing a groupId collect into one group, even when separated by
 * other groups' rows in the array. Group order follows the first occurrence of
 * each groupId. Vehicles are expected to be in newest-first order from the API.
 */
export function bucketByGroupId(vehicles: FlatVehicleDetection[]): VehicleGroup[] {
  const seen = new Map<string, VehicleGroup>();
  const ordered: VehicleGroup[] = [];

  for (const v of vehicles) {
    const existing = seen.get(v.groupId);
    if (existing) {
      existing.members.push(v);
    } else {
      const group: VehicleGroup = { key: v.groupId, tier: v.tier, score: v.score, members: [v] };
      seen.set(v.groupId, group);
      ordered.push(group);
    }
  }

  return ordered;
}
